<template>
    <div 
        class="pagination-container" 
        :class="containerClasses"
        v-if="shouldRender"
        data-testid="pagination" 
    >
        <v-divider></v-divider>
        <v-row justify="center" class="d-flex pa-0 ma-0 align-center justify-space-between" data-testid="pagination__row">
            <v-col :cols="!$store.state.mininavbar ? 7 : 8" class="pa-0" data-testid="pagination__col">
                <v-container class="pa-0 ma-0 d-flex" data-testid="pagination__container">
                    <v-pagination v-model="page" class="font-small" :length="pagecount" :total-visible="7" @input="emitPaginatedData" :disabled="disabled" data-testid="pagination__pagination"></v-pagination>
                </v-container>
            </v-col>
            <v-col cols="3" data-testid="pagination__col-go-to">
                <div class="d-flex align-center justify-end" data-testid="pagination__go-to-container">
                    <p class="ma-0 pa-0 px-2" data-testid="pagination__go-to-text">Go to this page</p>
                    <v-text-field
                        v-model="pageInput"
                        min="1"
                        class="mr-1 pagination__go-to-input"
                        hide-details
                        dense
                        outlined
                        type="number"
                        @change="handlePageInput"
                        :disabled="disabled"
                        data-testid="pagination__go-to-input"
                    />
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script>
export default {
    name: "Pagination",
    props: {
        list: {
            type: Array,
            default: () => [],
        },
        itemsperpage: {
            type: Number,
            default: 20,
        },
        value: {
            type: Number,
            default: 1,
        },
        lengthOverride: {
            type: Number,
            default: 0,
        },
        hideOnSinglePage: {
            type: Boolean,
            default: true,
        },
        inline: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            page: this.value || 1,
            pageInput: this.value || 1,
        };
    },
    watch: {
        value(newValue) {
            const normalized = Number(newValue) || 1;
            if (normalized !== this.page) {
                this.page = normalized;
            }
        },
        page(val) {
            this.pageInput = val; 
            this.$emit('input', this.page);
            this.emitPaginatedData();
        },
        list() {
            const pageAdjusted = this.ensureValidPage();
            if (!pageAdjusted) {
                this.emitPaginatedData();
            }
        },
        itemsperpage() {
            const pageAdjusted = this.ensureValidPage();
            if (!pageAdjusted) {
                this.emitPaginatedData();
            }
        },
        lengthOverride() {
            const pageAdjusted = this.ensureValidPage();
            if (!pageAdjusted) {
                this.emitPaginatedData();
            }
        }
    },
    methods: {
        ensureValidPage() {
            const totalPages = this.pagecount;
            let targetPage;

            if (totalPages <= 0) {
                targetPage = 1;
            } else if (this.page > totalPages) {
                targetPage = totalPages;
            } else if (this.page < 1) {
                targetPage = 1;
            } else {
                targetPage = this.page;
            }

            if (targetPage !== this.page) {
                this.page = targetPage;
                return true;
            }

            return false;
        },
        emitPaginatedData() {
            if (this.ensureValidPage()) {
                return;
            }
            const safePerPage = Math.max(1, Number(this.itemsperpage) || 1);
            const start = (this.page - 1) * safePerPage;
            const end = this.page * safePerPage;
            const paginatedData = this.list.slice(start, end);
            this.$emit('paginated-data', {
                data: paginatedData,
                page: this.page,
                pagecount: this.pagecount,
            });
        },
        handlePageInput(value) {
            const pageValue = parseInt(value, 10);
            if (!pageValue) {
                this.pageInput = this.page;
                return;
            }
            
            let finalPage = pageValue;
            if (pageValue > this.pagecount) {
                finalPage = this.pagecount;
            } else if (pageValue < 1) {
                finalPage = 1;
            }
            
            if (this.page !== finalPage) {
                this.page = finalPage;
            } else {
                // Reset input if it was invalid but page didn't change (e.g. typing 0 when page is 1)
                this.pageInput = this.page;
            }
        },
    },
    computed: {
        pagecount() {
            const override = Number(this.lengthOverride) || 0;
            if (override > 0) {
                return override;
            }
            const safePerPage = Math.max(1, Number(this.itemsperpage) || 1);
            const computed = Math.ceil((this.list || []).length / safePerPage);
            const result = computed > 0 ? computed : 1;
            return result;
        },
        shouldRender() {
            const render = !this.hideOnSinglePage ? this.pagecount >= 1 : this.pagecount > 1;
            return render;
        },
        containerClasses() {
            if (this.inline) {
                return 'pagination-container--inline';
            }
            return {
                'pagination-container--full': !this.$store.state.mininavbar,
                'pagination-container--mini': this.$store.state.mininavbar
            };
        },
    },
    mounted() {
        this.emitPaginatedData(); // Emit the first page of data when the component is mounted
    },
};
</script>

<style scoped>


.pagination-container {
    position: fixed;
    bottom: 0;
    right: 0;
    background: white;
    z-index: 100;
    transition: left 0.3s ease;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
}
.theme--dark .pagination-container {
    background: #1e1e1e;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.3);
}

.pagination-container--full {
    left: 250px;
}

.pagination-container--mini {
    left: 64px;
}

.pagination-container--inline {
    position: relative;
    left: unset;
    right: unset;
    bottom: unset;
    box-shadow: none;
    background: transparent;
}
.theme--dark .pagination-container--inline {
    background: transparent;
    box-shadow: none;
}

.pagination__go-to-input {
    max-width: var(--pagination-input-max-width) !important;
    min-width: var(--pagination-input-min-width);
}

.pagination__go-to-input ::v-deep input[type='number'] {
    -moz-appearance: textfield;
    min-width: 2.5ch;
}

.pagination__go-to-input ::v-deep input[type='number']::-webkit-outer-spin-button,
.pagination__go-to-input ::v-deep input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
</style>
